import { useState, type Dispatch, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { ArrowBackIosNew } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useArticleEditorPageRedirect } from '@widgets/articles/article-edit/helpers.ts';
import { Header } from '@widgets/header';
import { Search } from '@features/common';
import { useMobileVisibilityFilter } from '@features/programs';
import { Add, FilterIcon, Search as SearchIcon } from '@shared/assets';
import { useMobile } from '@shared/hooks';
import { useListTabs } from '@shared/hooks/use-list-tabs.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import type { ActiveTabType } from '@shared/types/common';
import { IconButton, NavTab, PageHead } from '@shared/ui';
import { FilterMobileActions } from '@shared/ui/filter-mobile-actions/index.ts';
import st from './styles.module.scss';

interface Props {
  showFilterBar: boolean;
  setShowFilterBar: Dispatch<SetStateAction<boolean>>;
  titles: Array<string>;
  clearFilterData: () => void;
  search: string;
  onSearch: (val: string) => void;
  isFilterActive: boolean;
}

export const ArticlesListHeader: React.FC<Props> = ({
  showFilterBar,
  setShowFilterBar,
  titles,
  clearFilterData,
  search,
  onSearch,
  isFilterActive,
}) => {
  const [isSearchMobile, setIsSearchMobile] = useState(false);
  const { permissions } = useModuleByCodeParam();
  const { mobileWithFilterOn } = useMobileVisibilityFilter(showFilterBar);
  const { isMobile } = useMobile();
  const { tabs, onTabClick, activeTab } = useListTabs();
  const { doSavePageUrl } = useArticleEditorPageRedirect();

  return (
    <>
      {!isSearchMobile && (
        <>
          <Header
            MCenter={<>{mobileWithFilterOn ? 'Фильтр' : 'Статьи'}</>}
            menuClassName={`${mobileWithFilterOn ? st.hide : ''}`}
          />

          <PageHead
            Left={
              <>
                {!mobileWithFilterOn &&
                  Object.keys(tabs).map((key) => (
                    <NavTab
                      isActive={key === activeTab}
                      key={key}
                      onClick={() => {
                        onTabClick(key as ActiveTabType);
                      }}
                    >
                      {tabs[key]}
                    </NavTab>
                  ))}
              </>
            }
            Right={
              <>
                {mobileWithFilterOn ? (
                  <FilterMobileActions
                    isFilterActive={isFilterActive}
                    onReset={clearFilterData}
                    onConfirm={() => setShowFilterBar(false)}
                  />
                ) : (
                  <>
                    {!isMobile ? (
                      <Search search={search} onSearch={onSearch} titles={titles} />
                    ) : (
                      <IconButton
                        variant="mobile"
                        onClick={() => setIsSearchMobile(!isSearchMobile)}
                      >
                        <SearchIcon />
                      </IconButton>
                    )}

                    <Tooltip title="Фильтр">
                      <IconButton
                        variant={`${showFilterBar || isFilterActive ? 'activeDark' : 'dark'}`}
                        onClick={() => {
                          setShowFilterBar((prev) => {
                            if (prev) {
                              setTimeout(clearFilterData, 125);
                            }
                            return !prev;
                          });
                        }}
                      >
                        <FilterIcon />
                      </IconButton>
                    </Tooltip>

                    {permissions.can_sell && activeTab === 'my' && (
                      <>
                        <Tooltip title="Добавить статью">
                          <Link to="new/edit" onClick={doSavePageUrl}>
                            <IconButton variant="dark">
                              <Add />
                            </IconButton>
                          </Link>
                        </Tooltip>
                      </>
                    )}
                  </>
                )}
              </>
            }
          />
        </>
      )}

      {isSearchMobile && (
        <>
          <Header MCenter={<>Поиск</>} />
          <PageHead
            Left={
              <IconButton
                variant="dark"
                onClick={() => {
                  setIsSearchMobile(!isSearchMobile);
                  onSearch('');
                }}
              >
                <ArrowBackIosNew fontSize="small" sx={{ transform: 'scale(0.8)' }} />
              </IconButton>
            }
            Right={
              <>
                <Search mobile search={search} onSearch={onSearch} titles={titles} />
              </>
            }
          />
        </>
      )}
    </>
  );
};
